import axios from 'axios';
import { getApiError } from '@/lib/utils';

// Auto-detect backend URL based on current hostname
const getBackendUrl = () => {
  const currentHost = window.location.hostname;
  if (currentHost.includes('.emergent.host')) return window.location.origin;
  if (currentHost.includes('.preview.')) return window.location.origin;
  if (currentHost === 'localhost' || currentHost === '127.0.0.1') {
    return process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  }
  return window.location.origin;
};

const API_URL = getBackendUrl();

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  // Bug fix: no default timeout meant a stalled request (slow query,
  // DB cold-start, dropped connection) left the UI's loading spinner
  // spinning forever instead of surfacing an error. Calls that
  // legitimately need longer (e.g. file import, report pack
  // generation) pass their own `timeout` override per-request.
  timeout: 60000,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // iter448 — per-session Region toggle: attach X-Region header so
  // the backend can ring-fence data (UK / UAE) without touching the
  // shared org settings row. See SettingsContext.setRegion().
  const sessionRegion = localStorage.getItem('session_region');
  if (sessionRegion && (sessionRegion === 'UK' || sessionRegion === 'UAE')) {
    config.headers['X-Region'] = sessionRegion;
  }
  return config;
});

// ── iter412 — Refresh-Token Rotation Race Guard ───────────────────
// Background: the backend rotates refresh tokens on every /auth/refresh
// call and triggers "reuse detection" if the same token is presented
// twice — revoking the entire token FAMILY (= kicks the user out of
// every tab and session). That detection used to fire as a false
// positive whenever:
//   • two parallel API calls both hit 401 → both axios interceptors
//     queued a /refresh in parallel, OR
//   • the proactive timer fired while a 401 was already in-flight, OR
//   • two browser tabs each ran their own proactive timer at the
//     same moment.
// The first call rotated the token, the second presented the now-
// revoked token, reuse detection kicked in, family revoked, user
// kicked mid-typing. (Reported for Martin Cassidy, 2026-06-11.)
//
// Mitigation:
//   1. In-tab SINGLETON: any caller that needs a refresh awaits the
//      same in-flight Promise. If a refresh just finished within the
//      last 5s, callers reuse the fresh access_token without issuing
//      a new /refresh.
//   2. Cross-tab DEDUP: each successful refresh stamps localStorage
//      with the new access_token + a high-resolution timestamp. Other
//      tabs notice the stamp and reuse it instead of firing their own
//      /refresh.
let refreshInFlight = null;
const REFRESH_REUSE_WINDOW_MS = 5000; // accept a sibling's refresh up to 5s old

async function performRefresh() {
  // Cross-tab piggyback: another tab may have just refreshed.
  const stampedAt = parseInt(localStorage.getItem('access_token_refreshed_at') || '0', 10);
  if (stampedAt && Date.now() - stampedAt < REFRESH_REUSE_WINDOW_MS) {
    const cached = localStorage.getItem('access_token');
    if (cached) return cached;
  }
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const response = await axios.post(`${getBackendUrl()}/api/auth/refresh`, {}, {
        withCredentials: true,
      });
      const { access_token } = response.data;
      if (access_token) {
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('access_token_refreshed_at', String(Date.now()));
      }
      return access_token;
    } finally {
      // Small grace window so trailing 401s within the same tick
      // collapse onto this same resolved value before clearing.
      setTimeout(() => { refreshInFlight = null; }, 200);
    }
  })();
  return refreshInFlight;
}

// Handle token refresh on 401, maintenance 503
// CRITICAL: When offline, do NOT redirect to login. Let the app handle it.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // ── Offline guard: if there's no response at all (network error),
    // do NOT attempt refresh or redirect. The calling code handles offline. ──
    if (!error.response) {
      return Promise.reject(error);
    }

    // Handle maintenance mode (503)
    if (error.response?.status === 503) {
      const detail = getApiError(error, '');
      if (detail.includes('maintenance')) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(error);
      }
    }

    // Don't try to refresh for auth routes
    const isAuthRoute = originalRequest.url?.includes('/auth/');

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthRoute) {
      originalRequest._retry = true;

      // Don't attempt refresh if offline
      if (!navigator.onLine) {
        return Promise.reject(error);
      }

      try {
        const access_token = await performRefresh();
        if (!access_token) throw new Error('refresh-returned-empty');
        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Token refresh failed — session expired
        if (navigator.onLine) {
          // Preserve offline data (offline_last_user, IndexedDB packages)
          // Only clear the expired auth tokens, not offline capabilities
          localStorage.removeItem('access_token');
          localStorage.removeItem('user');
          localStorage.removeItem('access_token_refreshed_at');
          // DON'T do a hard redirect — let React handle the auth state change
          // This prevents losing offline session data from a full page reload
          window.dispatchEvent(new CustomEvent('auth:session-expired'));
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// ── Proactive Token Refresh ──
const PROACTIVE_REFRESH_INTERVAL = 12 * 60 * 1000; // 12 minutes
let proactiveRefreshTimer = null;

function scheduleProactiveRefresh() {
  if (proactiveRefreshTimer) clearTimeout(proactiveRefreshTimer);
  const token = localStorage.getItem('access_token');
  if (!token) return;

  // iter412 — small jitter so two tabs don't fire on the exact same
  // millisecond and race each other to the /refresh endpoint.
  const jitter = Math.floor(Math.random() * 30_000); // 0–30s
  proactiveRefreshTimer = setTimeout(async () => {
    // Skip refresh if offline
    if (!navigator.onLine) {
      scheduleProactiveRefresh();
      return;
    }
    try {
      // iter412 — go through the singleton so the proactive refresh
      // and any in-flight 401 retry collapse onto a single /refresh.
      await performRefresh();
    } catch {
      // Silent fail — the 401 interceptor handles it on next API call
    }
    scheduleProactiveRefresh();
  }, PROACTIVE_REFRESH_INTERVAL + jitter);
}

// Start proactive refresh on load if user has a token
if (localStorage.getItem('access_token')) {
  scheduleProactiveRefresh();
}

// Re-schedule on every successful API response (user is active)
api.interceptors.response.use((response) => {
  if (localStorage.getItem('access_token')) {
    scheduleProactiveRefresh();
  }
  return response;
});

export function startProactiveRefresh() { scheduleProactiveRefresh(); }
export function stopProactiveRefresh() {
  if (proactiveRefreshTimer) { clearTimeout(proactiveRefreshTimer); proactiveRefreshTimer = null; }
}

export default api;
